import React, { useEffect, useState } from 'react'
import FunctionalityForm from './form'
import FunctionalityTable from './table'
import SecurityAdministrationLayout from '../../commons/SecurityAdministrationLayout'
import { getFromApi, postToApi, putToApi } from 'actions'
import { VALUES } from 'utils/constants'
import { buildFiltersDescriptors } from '../../commons/SearchFilters'
import omit from 'lodash/omit'
import { useStateValue } from 'context/store'
import { useApiRequest } from 'utils/customHooks/useApiRequest'
import { openSnackBarAction } from 'context/reducers/openSnackbarReducer'

const INITIAL_FUNCTIONALITY_FORM_STATE = {
   name: '',
   status: '',
   description: '',
   module: '',
   link: '',
   id: '',
   application: ''
}

const TITLE = {
   [VALUES.FORM_BEHAVIOR.REGISTER]: 'Registrar Funcionalidad',
   [VALUES.FORM_BEHAVIOR.SEARCH]: 'Consultar Funcionalidad',
   [VALUES.FORM_BEHAVIOR.EDIT]: 'Editar Funcionalidad'
}

const Functionality = ({ isSearch }) => {
   const [isShowingPopover, setPopoverProp] = useState(true)
   const [allActiveApplications, setAllActiveApplications] = useState([])
   const [modulesByAppId, setModulesByAppId] = useState([])

   const [formData, setFormData] = useState(INITIAL_FUNCTIONALITY_FORM_STATE)
   const [formBehavior, setFormBehavior] = useState(
      isSearch ? VALUES.FORM_BEHAVIOR.SEARCH : VALUES.FORM_BEHAVIOR.REGISTER
   )
   const [tableData, setTableData] = useState(VALUES.TABLE_DATA_INTIAL_STATE)
   const [paginationParams, setPaginationParams] = useState(VALUES.PAGING_ORDER_INITIAL_STATE)
   const [searchFilters, setSearchFilters] = useState({})
   const [options, setOptions] = useState([...VALUES.STATUS_OPTIONS])

   const [_, dispatch] = useStateValue()

   // ---------------- Clean Form -----------------
   function cleanForm() {
      setFormData({
         ...INITIAL_FUNCTIONALITY_FORM_STATE
      })
      setFormBehavior(isSearch ? VALUES.FORM_BEHAVIOR.SEARCH : VALUES.FORM_BEHAVIOR.REGISTER)
   }

   function clean() {
      setSearchFilters({})
      setTableData(VALUES.TABLE_DATA_INTIAL_STATE)
      setPopoverProp(true)
      setPaginationParams(VALUES.PAGING_ORDER_INITIAL_STATE)
      cleanForm()
   }

   useEffect(() => {
      clean()
   }, [isSearch]) // eslint-disable-line react-hooks/exhaustive-deps

   // ------------- Fetch all Initial Active Applications  -----------------
   async function _getAllActiveApplications() {
      const url = `${process.env.REACT_APP_APPLICATIONS_API}/applications/active`
      const activeApplications = await getFromApi(url)
      setAllActiveApplications(activeApplications)
   }

   const getAllActiveApplications = useApiRequest(
      _getAllActiveApplications,
      'Funcionalidades: Consultar Aplicaciones Activas'
   )

   useEffect(() => {
      getAllActiveApplications()
   }, [])

   // ------------ Fetch Modules by Application Id
   async function _getModulesByApplicationId(applicationId) {
      const payload = {
         applicationId,
         status: 'A',
         page: 0,
         size: 500,
         order: 'asc',
         columnOrder: 'name'
      }
      const url = `${process.env.REACT_APP_MODULES_API}/modules/search`
      const { data: searchedModules } = await getFromApi(url, payload)
      setModulesByAppId(searchedModules)
      const modulesFilters = searchedModules.map(app => ({
         label: app.name,
         value: app.id
      }))
      setOptions(prev => [...prev, ...modulesFilters])
   }

   const getModulesByApplicationId = useApiRequest(
      _getModulesByApplicationId,
      'Funcionalidades: Consultar Modulos por Aplicación'
   )

   // ------------ Search Functionlities -----------------
   const getFiltersDescriptors = filters => {
      let { module, ...otherFilters } = filters
      otherFilters = omit(otherFilters, ['application'])
      const descriptors =
         buildFiltersDescriptors(options)({
            ...otherFilters,
            module: module?.id
         }) || []
      return descriptors
   }

   const removeSearchFilter = async filter => {
      const remainingFilters = omit(searchFilters, [filter.filterName])
      await searchFunctionality(remainingFilters, VALUES.PAGING_ORDER_INITIAL_STATE)
   }

   async function _searchFunctionality(externalFilters, pagingFromTableParams = {}) {
      const filters = externalFilters ? externalFilters : searchFilters
      const paginationFilters = {
         ...paginationParams,
         ...pagingFromTableParams
      }
      setSearchFilters(filters)
      setPaginationParams(paginationFilters)
      const data = {}
      if (filters.name) {
         data.name = filters.name
      }
      if (filters.module) {
         data.moduleId = filters.module.id
      }
      if (filters.status) {
         data.status = filters.status
      }
      const payload = { ...data, ...paginationFilters }
      const url = `${process.env.REACT_APP_FUNCTIONALITIES_API}/functionalities/search`
      const searchedFunctionalities = await getFromApi(url, payload)
      setTableData(searchedFunctionalities)
      setFormData(INITIAL_FUNCTIONALITY_FORM_STATE)
   }

   const searchFunctionality = useApiRequest(_searchFunctionality, 'Funcionalidades: Consultar')

   // ------------ Register Functionlities -----------------
   async function _registerFunctionality() {
      const payload = {
         name: formData.name,
         description: formData.description,
         moduleId: formData.module.id,
         link: formData.link
      }
      const url = `${process.env.REACT_APP_FUNCTIONALITIES_API}/functionalities`
      const registeredFunctionality = await postToApi(url, payload)
      setTableData({
         ...VALUES.TABLE_DATA_INTIAL_STATE,
         data: [registeredFunctionality]
      })
      setFormData(INITIAL_FUNCTIONALITY_FORM_STATE)
      dispatch(openSnackBarAction(`Se guardó correctamente la Funcionalidad ${registeredFunctionality.name}`, 'info'))
   }

   const registerFunctionality = useApiRequest(_registerFunctionality, 'Funcionalidades: Registrar')

   // ------------ Edit Functionlities -----------------
   async function _editFunctionality() {
      const payload = {
         name: formData.name,
         description: formData.description,
         moduleId: formData.module.id,
         link: formData.link,
         status: formData.status
      }
      const url = `${process.env.REACT_APP_FUNCTIONALITIES_API}/functionalities/${formData.id}`
      const editedFunctionality = await putToApi(url, payload)
      const formBehavior = isSearch ? VALUES.FORM_BEHAVIOR.SEARCH : VALUES.FORM_BEHAVIOR.REGISTER
      setFormBehavior(formBehavior)
      setFormData(INITIAL_FUNCTIONALITY_FORM_STATE)
      setTableData({
         ...VALUES.TABLE_DATA_INTIAL_STATE,
         data: [editedFunctionality]
      })
      dispatch(openSnackBarAction(`Se actualizó correctamente la Funcionalidad ${editedFunctionality.name}`, 'info'))
   }

   const editFunctionality = useApiRequest(_editFunctionality, 'Funcionalidades: Editar')

   // -------------- Form Actions ---------------
   const actionSubmit = type => {
      setPopoverProp(false)
      if (type === VALUES.FORM_BEHAVIOR.REGISTER) {
         registerFunctionality()
      }
      if (type === VALUES.FORM_BEHAVIOR.EDIT) {
         editFunctionality()
      }
      if (type === VALUES.FORM_BEHAVIOR.SEARCH) {
         searchFunctionality(formData, VALUES.PAGING_ORDER_INITIAL_STATE)
      }
   }

   const actionEdit = rowData => {
      setFormBehavior(VALUES.FORM_BEHAVIOR.EDIT)
      setPopoverProp(true)
      setFormData({
         id: rowData.id,
         name: rowData.name,
         description: rowData.description,
         application: rowData.module.application,
         module: rowData.module,
         link: rowData.link,
         status: rowData.status
      })
   }

   return (
      <SecurityAdministrationLayout
         title={TITLE[formBehavior]}
         form={
            <FunctionalityForm
               formData={formData}
               setForm={setFormData}
               modules={modulesByAppId}
               setModules={setModulesByAppId}
               actionSubmit={actionSubmit}
               formBehavior={formBehavior}
               cleanCallback={cleanForm}
               applications={allActiveApplications}
               getModulesByApplicationId={getModulesByApplicationId}
            />
         }
         table={
            <FunctionalityTable
               isSearch={isSearch}
               order={paginationParams.order}
               functionalities={tableData}
               searchFunctionalitites={searchFunctionality}
               actionEdit={actionEdit}
               cleanCallback={clean}
            />
         }
         popoverProp={isShowingPopover}
         setPopoverProp={setPopoverProp}
         searchFilters={getFiltersDescriptors(searchFilters)}
         onRemoveFilter={removeSearchFilter}
      />
   )
}

export default Functionality
